import { useState } from 'react';
import { WalletTransactionType } from '@klaro7/shared-types';
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@klaro7/ui';

import { useWallet, useWalletTransactions } from '@/features/wallet/hooks/useWallet';

function formatCurrency(value: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(value: string | Date): string {
  return new Date(value).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function EarningsPage() {
  const { data: wallet, isLoading: walletLoading } = useWallet();

  const [page, setPage] = useState(1);
  const { data: transactionsData, isLoading: txLoading } = useWalletTransactions({
    page,
    limit: 10,
    type: WalletTransactionType.CALL_CHARGE,
  });

  const transactions = transactionsData?.data ?? [];
  const pagination = transactionsData?.pagination;
  const currency = wallet?.currency ?? 'EUR';
  const balance = wallet?.balance ?? 0;

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 p-8">
        <div className="relative z-10">
          <h1 className="text-2xl font-bold text-white">I tuoi Guadagni</h1>
          <p className="mt-1 text-brand-cyan">Saldo disponibile e storico delle consulenze pagate</p>
        </div>
        <div className="absolute right-0 top-0 h-full w-1/3 opacity-10">
          <svg viewBox="0 0 200 200" className="h-full w-full" fill="none">
            <circle cx="160" cy="40" r="60" fill="white" />
            <circle cx="100" cy="140" r="80" fill="white" />
            <circle cx="180" cy="160" r="40" fill="white" />
          </svg>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-neutral-500">Saldo disponibile</CardTitle>
            </CardHeader>
            <CardContent>
              {walletLoading ? (
                <Spinner size="sm" />
              ) : (
                <p className="text-3xl font-bold text-neutral-900">{formatCurrency(balance, currency)}</p>
              )}
              <p className="mt-1 text-xs text-neutral-500">
                I prelievi saranno disponibili a breve
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-neutral-500">Consulenze pagate</CardTitle>
            </CardHeader>
            <CardContent>
              {txLoading && !transactionsData ? (
                <Spinner size="sm" />
              ) : (
                <p className="text-3xl font-bold text-neutral-900">{pagination?.total ?? transactions.length}</p>
              )}
              <p className="mt-1 text-xs text-neutral-500">Totale chiamate fatturate</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Storico Guadagni</CardTitle>
          </CardHeader>
          <CardContent>
            {txLoading ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="py-12 text-center">
                <p className="font-medium text-neutral-600">Nessun guadagno ancora</p>
                <p className="mt-1 text-sm text-neutral-400">
                  I tuoi guadagni dalle chiamate appariranno qui
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between rounded-lg border border-neutral-200 p-4"
                    >
                      <div>
                        <p className="font-medium text-neutral-900">
                          {tx.description || 'Guadagno chiamata'}
                        </p>
                        <p className="text-xs text-neutral-500">{formatDate(tx.createdAt)}</p>
                      </div>
                      <p className="text-lg font-semibold text-green-600">
                        +{formatCurrency(tx.amount, currency)}
                      </p>
                    </div>
                  ))}
                </div>

                {pagination && pagination.totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!pagination.hasPrevPage}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Precedente
                    </Button>
                    <span className="text-sm text-neutral-500">
                      Pagina {pagination.page} di {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!pagination.hasNextPage}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Successiva
                    </Button>
                  </div>
                )}
              </>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
